import React, { useContext } from "react";
import PropTypes from "prop-types";
import { Tab, Tabs, Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import { UserContext } from "../../context/userContext";
import { IS_FORD_BRAND } from "../../../utils/EmpUtil";

export const LINCOLN_TAB_FONT_FAMILY = "ProximaNovaSemibold, Arial, sans-serif";

const EMPTabPanel = (props) => {
  const { children, value, index } = props;

  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      sx={{ mt: 6, padding: 0, minHeight: "400px" }}
    >
      {value === index && <Box>{children}</Box>}
    </Box>
  );
};

EMPTabPanel.propTypes = {
  children: PropTypes.node,
  value: PropTypes.number,
  index: PropTypes.number,
};

EMPTabPanel.defaultProps = {
  children: null,
  value: 0,
  index: 0,
};

const GridTabs = styled(Tabs, {
  shouldForwardProp: (prop) => prop !== "isSegmented" && prop !== "tabCount",
})(({ theme, isSegmented, tabCount }) => ({
  ...(isSegmented && {
    backgroundColor: "#ECEEEF",
    borderRadius: "8px",
    minHeight: 44,
    padding: "4px",
  }),
  "& .MuiTabs-flexContainer": {
    display: "grid",
    gridTemplateColumns: isSegmented
      ? `repeat(${Math.max(tabCount, 1)}, minmax(0, 1fr))`
      : "repeat(12, 1fr)",
    rowGap: theme.spacing(0),
    columnGap: isSegmented ? 0 : theme.spacing(4),
  },

  "& .MuiTabs-indicator": {
    height: 0,
    backgroundColor: "transparent",
  },
}));

const getGridColumnSpan = (tabCount) => {
  if (tabCount === 1) {
    return 12;
  }

  if (tabCount === 2) {
    return 6;
  }

  if (tabCount < 4) {
    return 3;
  }

  return 2;
};

const GridTab = styled(Tab, {
  shouldForwardProp: (prop) =>
    prop !== "tabCount" && prop !== "isFordBrand" && prop !== "isSegmented",
})(({ tabCount, isFordBrand, isSegmented }) => {
  if (isSegmented) {
    return {
      "&.MuiButtonBase-root": {
        gridColumn: "auto",
        minWidth: 0,
        maxWidth: "none",
        minHeight: 36,
        padding: "4px 8px",
        marginRight: 0,
        alignItems: "center",
        color: "#000000",
        fontFamily: "ProximaNovaRegular, Arial, sans-serif",
        fontSize: "16px",
        fontWeight: 400,
        lineHeight: "24px",
        textTransform: "none",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        border: "1px solid transparent",
        borderRadius: "4px",
      },
      "&.MuiButtonBase-root.Mui-selected": {
        backgroundColor: "#FFFFFF",
        color: "#485B65",
        fontFamily: LINCOLN_TAB_FONT_FAMILY,
        fontWeight: 600,
        borderColor: "#485B65",
        boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.12)",
      },
      "&.MuiButtonBase-root.Mui-focusVisible": {
        outline: "2px solid #485B65",
        outlineOffset: "-3px",
      },
    };
  }

  const unselectedColor = isFordBrand
    ? "#333333"
    : "var(--color-text-onlight-interactive, #485B65)";

  const selectedColor = isFordBrand
    ? "#0562D2"
    : "var(--color-text-onlight-interactive, #485B65)";

  return {
    "&.MuiButtonBase-root": {
      marginRight: 0,
      gridColumn: `span ${getGridColumnSpan(tabCount)}`,
      overflow: "hidden",
      color: unselectedColor,
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      fontFamily: isFordBrand
        ? "var(--body-1-regular-font-family, FordF1Regular)"
        : LINCOLN_TAB_FONT_FAMILY,
      fontSize: "var(--subtitle-semibold-font-size, 20px)",
      fontStyle: "normal",
      fontWeight: isFordBrand
        ? "var(--body-1-regular-font-weight, 400)"
        : "var(--subtitle-semibold-font-weight, 600)",
      lineHeight: "var(--subtitle-semibold-line-height, 28px)",
      borderBottom: `var(--border-width-m, 3px) solid ${unselectedColor}`,
    },

    "&.MuiButtonBase-root.Mui-selected": {
      color: selectedColor,
      borderBottom: `var(--border-width-m, 3px) solid ${selectedColor}`,
    },
  };
});

const EMPTabs = ({ tabItems, activeTab, onTabChange, variant }) => {
  const { user } = useContext(UserContext) || {};
  const isFordBrand = IS_FORD_BRAND(user?.brand);
  const isSegmented = variant === "segmented";

  const handleChange = (event, newValue) => {
    onTabChange(newValue);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <GridTabs
        aria-label="Tabs"
        variant="fullWidth"
        scrollButtons={false}
        value={activeTab}
        onChange={handleChange}
        isSegmented={isSegmented}
        tabCount={tabItems.length}
      >
        {tabItems.map((item) => (
          <GridTab
            disableRipple
            label={item.title}
            key={item.title}
            tabCount={tabItems.length}
            isFordBrand={isFordBrand}
            isSegmented={isSegmented}
          />
        ))}
      </GridTabs>

      {tabItems.map((item, index) => (
        <EMPTabPanel value={activeTab} index={index} key={item.title}>
          {item.content}
        </EMPTabPanel>
      ))}
    </Box>
  );
};

EMPTabs.propTypes = {
  tabItems: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      content: PropTypes.node,
    })
  ),
  activeTab: PropTypes.number,
  onTabChange: PropTypes.func,
  variant: PropTypes.oneOf(["underlined", "segmented"]),
};

EMPTabs.defaultProps = {
  tabItems: [],
  activeTab: 0,
  onTabChange: undefined,
  variant: "underlined",
};

export default EMPTabs;
